import { useEffect, useRef, useCallback } from "react";
import useAuthStore from "@/store/useAuthStore";
import { resolveApiBaseUrl } from "@/utils/apiBase";

// ─── Default WebSocket URL ──────────────────────────────────────────────────
// BUG #42 fix: WebSocket URL non veniva passato, connettiti alla stessa
// origine del server API (protocollo ws:// o wss:// in base all'API base).
const API_BASE: string = resolveApiBaseUrl();

export const WS_URL: string = API_BASE.replace(/^http/, "ws");

// ─── Discriminated union for all incoming WS message types ───────────────────

export type WsDmMessageIncoming = {
  type: "dm_message";
  threadId: string;
  message: {
    id: string;
    thread_id: string;
    sender_id: string;
    body: string;
    created_at: string;
  };
};

export type WsPresenceMessage = {
  type: "presence";
  userId: string;
  online: boolean;
};

export type WsSubscriptionUpdatedMessage = {
  type: "subscription_updated";
  tier: string;
  plan_code?: string | null;
  source?: "admin" | "self" | "stripe";
};

export type WsJoinThreadMessage = {
  type: "join_thread";
  threadId: string;
};

export type WsErrorMessage = {
  type: "error";
  message: string;
};

export type WsPingMessage = { type: "ping" };
export type WsPongMessage = { type: "pong" };

/**
 * Union discriminata di tutti i messaggi WebSocket in ingresso.
 * Usare msg.type come discriminante nei consumer.
 */
export type WsIncomingMessage =
  | WsDmMessageIncoming
  | WsPresenceMessage
  | WsSubscriptionUpdatedMessage
  | WsJoinThreadMessage
  | WsErrorMessage
  | WsPingMessage
  | WsPongMessage;

// ─── Outgoing messages ────────────────────────────────────────────────────────

export type WsOutgoingMessage =
  | { type: "join_thread"; threadId: string }
  | { type: "ping" }
  | { type: string; [key: string]: unknown }; // escape hatch per usi futuri

type MessageHandler = (msg: WsIncomingMessage) => void;

export interface UseWebSocketOptions {
  /** URL WebSocket. Se omesso/null/undefined la connessione non viene avviata. */
  url?: string | null;
  onMessage?: MessageHandler;
  onConnect?: () => void;
  onDisconnect?: () => void;
  reconnectDelay?: number;
}

/**
 * useWebSocket
 *
 * Manages a WebSocket connection with automatic reconnection.
 * Sends auth payload { type: 'auth', token } on open (server expects this,
 * NOT a query-string token). Connects to url + '/ws' path.
 * Defaults to WS_URL (derived from API base) when no url is provided.
 * Canonical path: import { useWebSocket } from '@/hooks/infrastructure/useWebSocket';
 */
export function useWebSocket({
  url = WS_URL,
  onMessage,
  onConnect,
  onDisconnect,
  reconnectDelay = 3000,
}: UseWebSocketOptions): { send: (msg: WsOutgoingMessage) => void } {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const authSent = useRef(false);
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 10;

  const connect = useCallback(() => {
    if (!url) return;

    // Rate-limit: non tentare più di MAX_RECONNECT_ATTEMPTS riconnessioni
    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      console.warn("[useWebSocket] Max reconnect attempts reached, giving up");
      return;
    }

    const token = useAuthStore.getState().accessToken;
    // Nessun token → niente connessione: il server chiuderebbe comunque la
    // socket e riempiremmo la console di "WebSocket is closed before the
    // connection is established". Ci ricolleghiamo al login (vedi effetto sotto).
    if (!token) return;

    const wsUrl = `${url}/ws`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      authSent.current = false;

      ws.onopen = () => {
        reconnectAttempts.current = 0; // reset counter on successful connection
        if (token) {
          ws.send(JSON.stringify({ type: "auth", token }));
          authSent.current = true;
        }
        onConnect?.();
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WsIncomingMessage;
          onMessage?.(msg);
        } catch {
          console.warn("[useWebSocket] Non-JSON message received:", event.data);
        }
      };

      ws.onclose = (event) => {
        onDisconnect?.();
        if (isMountedRef.current && event.code !== 4001) {
          reconnectAttempts.current++;
          // Exponential backoff: 3s, 6s, 12s, 24s... max 60s
          const delay = Math.min(
            reconnectDelay * Math.pow(2, reconnectAttempts.current - 1),
            60000,
          );
          reconnectTimer.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        // Don't call ws.close() here — onerror is always followed by onclose.
        // Calling close() manually can cause double-close issues.
      };
    } catch {
      // WebSocket constructor can throw if URL is invalid
      if (isMountedRef.current) {
        reconnectAttempts.current++;
        const delay = Math.min(
          reconnectDelay * Math.pow(2, reconnectAttempts.current - 1),
          60000,
        );
        reconnectTimer.current = setTimeout(connect, delay);
      }
    }
  }, [url, onMessage, onConnect, onDisconnect, reconnectDelay]);

  useEffect(() => {
    isMountedRef.current = true;
    connect();
    return () => {
      isMountedRef.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      const ws = wsRef.current;
      if (ws) {
        // FIX CONSOLE: chiudere una socket ancora in CONNECTING fa loggare
        // al browser "WebSocket is closed before the connection is
        // established". Se è CONNECTING, aspetta l'handshake e chiudi dopo.
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        } else if (ws.readyState === WebSocket.CONNECTING) {
          ws.onopen = () => ws.close();
        }
      }
    };
  }, [connect]);

  // Riconnetti quando l'utente effettua il login (senza token la socket
  // non viene aperta — vedi guard in connect())
  useEffect(() => {
    const unsub = useAuthStore.subscribe((state, prev) => {
      if (state.accessToken && !prev.accessToken) {
        reconnectAttempts.current = 0;
        connect();
      }
    });
    return unsub;
  }, [connect]);

  // QW4: Disconnetti WebSocket quando la pagina va in background (risparmio batteria)
  useEffect(() => {
    const onVisibility = () => {
      if (!isMountedRef.current) return;
      if (document.visibilityState === "visible") {
        // Riconnetti solo se non già connesso
        if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
          connect();
        }
      } else {
        // Pagina in background: chiudi per risparmiare batteria e dati
        if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        const ws = wsRef.current;
        if (ws) {
          // FIX CONSOLE: evita il warning "closed before the connection is
          // established" su socket ancora in CONNECTING.
          if (ws.readyState === WebSocket.OPEN) ws.close();
          else if (ws.readyState === WebSocket.CONNECTING)
            ws.onopen = () => ws.close();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [connect]);

  const send = useCallback((msg: WsOutgoingMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      console.warn("[useWebSocket] Cannot send — socket not open");
    }
  }, []);

  return { send };
}
